import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Zap, Sparkles, Check, ArrowRight, Loader2, Clock, Crown, Rocket } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

/**
 * Upgrade page — sells Affynix Pro 30-day pass (€120) and two one-off research products:
 *   • €64 Express Research — instant (Fast Lane)
 *   • €59 Standard Research — priority queue (~50% faster than free wait)
 *
 * Clicking any CTA hits POST /api/payments/checkout/session and redirects to Stripe.
 * If Stripe redirects back with ?cancelled=1, we surface a small "Payment cancelled" notice.
 */
export default function Upgrade() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [packages, setPackages] = useState([]);
    const [loadingPkg, setLoadingPkg] = useState(null);
    const [error, setError] = useState("");
    const cancelled = searchParams.get("cancelled") === "1";

    const isPro = user?.tier === "pro";
    const expressCredits = (user?.research_express_credits || 0) + (user?.one_off_credits || 0);  // include legacy
    const standardCredits = user?.research_standard_credits || 0;
    let proExpiresInDays = null;
    if (user?.pro_expires_at) {
        const ms = new Date(user.pro_expires_at).getTime() - Date.now();
        if (ms > 0) proExpiresInDays = Math.ceil(ms / (1000 * 60 * 60 * 24));
    }

    useEffect(() => {
        let active = true;
        api.get("/payments/packages").then((res) => {
            if (active) setPackages(res.data.packages || []);
        }).catch(() => {});
        return () => { active = false; };
    }, []);

    const checkout = async (packageId) => {
        setLoadingPkg(packageId);
        setError("");
        try {
            const res = await api.post("/payments/checkout/session", {
                package_id: packageId,
                origin_url: window.location.origin,
            });
            if (res.data?.url) {
                window.location.href = res.data.url;
            } else {
                setError("Could not start checkout. Try again in a moment.");
                setLoadingPkg(null);
            }
        } catch (e) {
            setError(e?.response?.data?.detail || "Checkout failed. Try again.");
            setLoadingPkg(null);
        }
    };

    const pkgPro = packages.find((p) => p.id === "pro_30day");
    const pkgProPlus = packages.find((p) => p.id === "pro_plus_30day");
    const pkgElite = packages.find((p) => p.id === "pro_elite_30day");
    const pkgExpress = packages.find((p) => p.id === "research_express");
    const pkgStandard = packages.find((p) => p.id === "research_standard");

    return (
        <div className="space-y-8" data-testid="upgrade-page">
            <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">
                    // Pricing
                </div>
                <h1 className="font-heading font-black tracking-tighter text-4xl text-zinc-50 leading-tight">
                    Research, delivered your way.
                </h1>
                <p className="text-base text-zinc-400 leading-relaxed mt-3 max-w-2xl">
                    Free accounts wait 2–20 minutes per research request. Pay for speed, pay per delivery,
                    or subscribe for unlimited monthly access. All AI-generated dossiers ship to your inbox.
                </p>
            </div>

            {/* Current status strip */}
            <div className="border border-zinc-800 bg-zinc-950 p-4 flex items-center gap-3 flex-wrap" data-testid="current-status">
                <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Your plan</div>
                {isPro ? (
                    <span className="inline-flex items-center gap-2 border border-amber-500/50 bg-amber-500/10 px-3 py-1 font-mono text-xs uppercase tracking-wider text-amber-300">
                        <Zap className="w-3.5 h-3.5" /> Pro · {user?.pro_daily_limit === -1 ? "unlimited" : `${user?.pro_daily_limit || 2}/day`}
                        {proExpiresInDays !== null && <span className="text-zinc-500">· {proExpiresInDays}d left</span>}
                    </span>
                ) : (
                    <span className="inline-flex items-center gap-2 border border-zinc-700 bg-zinc-900 px-3 py-1 font-mono text-xs uppercase tracking-wider text-zinc-400">
                        Free
                    </span>
                )}
                {expressCredits > 0 && (
                    <span className="inline-flex items-center gap-2 border border-amber-500/40 bg-amber-500/10 px-3 py-1 font-mono text-xs uppercase tracking-wider text-amber-300" data-testid="express-credits-badge">
                        <Zap className="w-3.5 h-3.5" /> {expressCredits} Express
                    </span>
                )}
                {standardCredits > 0 && (
                    <span className="inline-flex items-center gap-2 border border-emerald-500/50 bg-emerald-500/10 px-3 py-1 font-mono text-xs uppercase tracking-wider text-emerald-300" data-testid="standard-credits-badge">
                        <Sparkles className="w-3.5 h-3.5" /> {standardCredits} Standard
                    </span>
                )}
            </div>

            {cancelled && (
                <div className="border border-amber-500/40 bg-amber-500/[0.05] p-3 text-sm text-amber-200 font-mono" data-testid="cancel-notice">
                    Payment cancelled — no charge was made.
                </div>
            )}
            {error && (
                <div className="border border-red-500/40 bg-red-500/[0.05] p-3 text-sm text-red-200 font-mono" data-testid="checkout-error">
                    {error}
                </div>
            )}

            {/* Subscription tier — 3 cards */}
            <div>
                <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">// Monthly subscriptions</h2>
                <div className="grid md:grid-cols-3 gap-5">
                    <PricingCard
                        pkg={pkgPro}
                        icon={<Zap className="w-5 h-5 text-amber-400" />}
                        accent="amber"
                        priceSuffix="/ 30 days"
                        bullets={[
                            "2 research deliveries per day",
                            "Fast Lane included — instant analyst",
                            "All premium features unlocked",
                            "30 days · auto-extends on repurchase",
                            "Cancel anytime — just don't repurchase",
                        ]}
                        onCheckout={() => checkout("pro_30day")}
                        loading={loadingPkg === "pro_30day"}
                        testid="card-pro_30day"
                    />
                    <PricingCard
                        pkg={pkgProPlus}
                        icon={<Crown className="w-5 h-5 text-amber-400" />}
                        accent="amber"
                        highlight="Most popular"
                        priceSuffix="/ 30 days"
                        bullets={[
                            "5 research deliveries per day",
                            "Fast Lane included — instant analyst",
                            "All Pro features + priority support",
                            "30 days · auto-extends on repurchase",
                            "Sweet spot for active sales/BD teams",
                        ]}
                        onCheckout={() => checkout("pro_plus_30day")}
                        loading={loadingPkg === "pro_plus_30day"}
                        testid="card-pro_plus_30day"
                    />
                    <PricingCard
                        pkg={pkgElite}
                        icon={<Rocket className="w-5 h-5 text-amber-400" />}
                        accent="amber"
                        priceSuffix="/ 30 days"
                        bullets={[
                            "Unlimited research deliveries",
                            "Fast Lane included — instant analyst",
                            "Priority support · direct ops channel",
                            "Built for agencies & VC scout teams",
                            "Cancel anytime — just don't repurchase",
                        ]}
                        onCheckout={() => checkout("pro_elite_30day")}
                        loading={loadingPkg === "pro_elite_30day"}
                        testid="card-pro_elite_30day"
                    />
                </div>
            </div>

            {/* One-off — 2 cards */}
            <div>
                <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">// One-off purchases</h2>
                <div className="grid md:grid-cols-2 gap-5">
                    <PricingCard
                        pkg={pkgExpress}
                        icon={<Zap className="w-5 h-5 text-amber-400" />}
                        accent="amber"
                        priceSuffix="one-time"
                        bullets={[
                            "1 instant research delivery",
                            "Fast Lane — starts in 60 seconds",
                            "No subscription, no recurring charge",
                            "Credit never expires",
                            "Perfect for time-critical research",
                        ]}
                        onCheckout={() => checkout("research_express")}
                        loading={loadingPkg === "research_express"}
                        testid="card-research_express"
                    />
                    <PricingCard
                        pkg={pkgStandard}
                        icon={<Clock className="w-5 h-5 text-emerald-400" />}
                        accent="emerald"
                        priceSuffix="one-time"
                        bullets={[
                            "1 priority-queue research",
                            "~50% faster wait than free tier",
                            "Same AI quality as Pro / Express",
                            "Credit never expires",
                            "Budget option if you can wait a bit",
                        ]}
                        onCheckout={() => checkout("research_standard")}
                        loading={loadingPkg === "research_standard"}
                        testid="card-research_standard"
                    />
                </div>
            </div>

            <div className="text-center">
                <button
                    onClick={() => navigate("/app/requests")}
                    className="text-sm text-zinc-500 hover:text-zinc-300 font-mono uppercase tracking-wider"
                    data-testid="back-to-requests-btn"
                >
                    ← Back to requests
                </button>
            </div>
        </div>
    );
}

function PricingCard({ pkg, icon, accent, highlight, bullets, onCheckout, loading, testid, priceSuffix }) {
    if (!pkg) return <div className="border border-zinc-800 bg-zinc-950 p-6 h-72" />;
    const accentBorder = accent === "amber" ? "border-amber-500/40" : "border-emerald-500/40";
    const accentBtn = accent === "amber" ? "bg-amber-500 hover:bg-amber-400 text-black" : "bg-emerald-500 hover:bg-emerald-400 text-black";
    return (
        <div className={`border ${accentBorder} bg-zinc-950 p-6 flex flex-col`} data-testid={testid}>
            <div className="flex items-center gap-2 mb-2">
                {icon}
                <h2 className="font-heading font-bold text-base text-zinc-50 flex-1">{pkg.name}</h2>
                {highlight && (
                    <span className="font-mono text-[9px] uppercase tracking-wider text-amber-400 border border-amber-500/40 px-2 py-0.5">
                        {highlight}
                    </span>
                )}
            </div>
            <div className="flex items-baseline gap-2 mt-3">
                <span className="font-heading text-4xl font-black tracking-tight text-zinc-50">€{pkg.amount.toFixed(0)}</span>
                <span className="text-sm text-zinc-500 font-mono">{priceSuffix}</span>
            </div>
            <p className="text-xs text-zinc-500 mt-2 leading-relaxed">{pkg.description}</p>
            <ul className="space-y-2 mt-5 text-sm text-zinc-300 flex-1">
                {bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2">
                        <Check className={`w-4 h-4 mt-0.5 shrink-0 ${accent === "amber" ? "text-amber-400" : "text-emerald-400"}`} />
                        <span>{b}</span>
                    </li>
                ))}
            </ul>
            <button
                onClick={onCheckout}
                disabled={loading}
                className={`mt-6 ${accentBtn} disabled:opacity-50 disabled:cursor-not-allowed font-bold text-sm uppercase tracking-wider px-5 py-3 flex items-center justify-center gap-2`}
                data-testid={`${testid}-checkout-btn`}
            >
                {loading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Starting checkout…</>
                ) : (
                    <>Buy · €{pkg.amount.toFixed(0)} <ArrowRight className="w-4 h-4" /></>
                )}
            </button>
        </div>
    );
}
